import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { defaultInstance } from '../api/httpClient';
import type { Zone } from '../api/generated';

/**
 * The tenant's zone catalog (SAM-946): GET/POST/PUT/DELETE /crud/zone. A tenant has a handful of zones, so one page
 * of 500 is the whole list. `enabled` keeps the call off until the catalog exists (ZONES_ENABLED).
 */
const ZONES_KEY = ['zones'] as const;
const LIST_LIMIT = 500;

type Page<T> = { data?: T[] } | T[];
const rowsOf = <T,>(res: unknown): T[] => {
  const page = res as Page<T> | undefined;
  return Array.isArray(page) ? page : page?.data ?? [];
};

export const useZones = (enabled = true) =>
  useQuery({
    queryKey: ZONES_KEY,
    queryFn: async () =>
      rowsOf<Zone>(await defaultInstance({ url: '/zone', method: 'GET', params: { limit: LIST_LIMIT, page: 1 } })),
    enabled,
  });

export const useCreateZone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Pick<Zone, 'name'>) => defaultInstance({ url: '/zone', method: 'POST', data }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ZONES_KEY }),
  });
};

export const useUpdateZone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Pick<Zone, 'name'> }) =>
      defaultInstance({ url: `/zone/${id}`, method: 'PUT', data }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ZONES_KEY }),
  });
};

export const useDeleteZone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => defaultInstance({ url: `/zone/${id}`, method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ZONES_KEY }),
  });
};
